import { useQuery } from '@tanstack/react-query';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts';
import { api } from '../../services/api';
import { fmtMoney } from './format';
import { c, chart } from '../../theme';

// ── What the lens is, as a share of everything ───────────────────────────────
//
// A lens is saved filter state, so every other panel on this page already draws
// the lens correctly -- OvertimeTrend under lens=fleet IS the fleet maintenance
// trend. What a filtered chart cannot show is the DENOMINATOR. Fleet overtime
// rising $40k tells you nothing on its own: if total overtime rose the same
// week, fleet did not become a bigger problem, the week did.
//
// So this is the one reading the lens takes away and does not give back. Two
// calls to the same /trend endpoint -- one carrying the role filter, one
// carrying only the location scope -- and the ratio between them.
//
// THE DENOMINATOR KEEPS THE LOCATION SCOPE AND DROPS THE ROLE FILTER. If you
// are looking at one group, "share of total" means share of that group's
// overtime, not of the company's; comparing a group's fleet cost against a
// company-wide total would quietly shrink every number on the chart. Division
// is a cost-center filter, so it stays; lens and labor category are role
// filters, so they go.
//
// ITERATE THE DENOMINATOR'S BUCKETS, NOT THE NUMERATOR'S. A pay period with no
// fleet overtime is absent from the filtered series entirely, and drawing only
// the buckets that came back would silently close the gap and draw a flat line
// across a week that was actually zero. The full series comes from the
// unfiltered call; a missing numerator bucket is 0%, which is a fact, not a gap.
//
// A bucket whose TOTAL is zero has no share at all -- not 0% -- so it is null
// and Recharts breaks the line there rather than plotting a number nobody can
// defend.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// 'YYYY-MM' -> 'Jul 2026'   |   'YYYY-MM-DD' -> 'Jul 28'
function labelFor(bucket, grain) {
  if (!bucket) return '';
  if (grain === 'month') {
    const [y, m] = bucket.split('-');
    return `${MONTHS[Number(m) - 1] || m} ${y}`;
  }
  const d = new Date(`${bucket}T00:00:00`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const pct = (v) => (v === null || v === undefined ? '—' : `${v.toFixed(1)}%`);

export default function LensShareTrend({ grain, filterQ = '', baseQ = '', label = 'This lens' }) {
  const limit = grain === 'month' ? 13 : 12;

  // The filtered series. Same endpoint, same grain, same limit as the trend
  // beside it, so the two charts cannot disagree about which buckets exist.
  const num = useQuery({
    queryKey: ['ot-trend-lens', grain, filterQ],
    queryFn:  () => api.get(`/overtime/trend?grain=${grain}&limit=${limit}${filterQ}`),
  });

  // The denominator: location scope only.
  const den = useQuery({
    queryKey: ['ot-trend-base', grain, baseQ],
    queryFn:  () => api.get(`/overtime/trend?grain=${grain}&limit=${limit}${baseQ}`),
  });

  const isLoading = num.isLoading || den.isLoading;
  const error     = num.error || den.error;

  const lensBy = new Map((num.data?.points || []).map((p) => [p.bucket, p]));
  const rows = (den.data?.points || []).map((t) => {
    const part  = Number(lensBy.get(t.bucket)?.ot_amount || 0);
    const total = Number(t.ot_amount || 0);
    return {
      bucket: t.bucket,
      label:  labelFor(t.bucket, grain),
      part,
      total,
      share:  total > 0 ? (part / total) * 100 : null,
    };
  });

  const shares = rows.map((r) => r.share).filter((v) => v !== null);
  const mean   = shares.length ? shares.reduce((a, b) => a + b, 0) / shares.length : null;

  const heading = grain === 'month'
    ? `${label} as a share of all overtime — by month`
    : `${label} as a share of all overtime — by pay period`;

  return (
    <>
      <div style={s.title}>{heading}</div>
      <div style={s.card}>
        {isLoading ? (
          <div style={s.empty}>Loading…</div>
        ) : error ? (
          <div style={s.empty}>{String(error.message || error)}</div>
        ) : !rows.length ? (
          <div style={s.empty}>No overtime loaded yet.</div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
                <CartesianGrid vertical={false} stroke={chart.grid} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: chart.axisText }}
                       axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => `${v}%`} domain={[0, 'auto']} width={44}
                       tick={{ fontSize: 10, fill: chart.axisText }}
                       axisLine={false} tickLine={false} />
                {/* The average is the only thing that makes a single line
                    readable as high or low rather than merely present. */}
                {mean !== null && (
                  <ReferenceLine y={mean} stroke={chart.deselected} strokeDasharray="4 3" />
                )}
                <Tooltip
                  cursor={{ stroke: chart.grid }}
                  formatter={(v, _n, p) => [
                    `${pct(v)} · ${fmtMoney(p?.payload?.part)} of ${fmtMoney(p?.payload?.total)}`,
                    label,
                  ]}
                  labelFormatter={(l) => l}
                  contentStyle={{ fontSize: '12px', borderRadius: '8px', border: `1px solid ${chart.tooltipBorder}` }}
                />
                {/* connectNulls stays false: a period with no overtime at all is
                    a break in the series, and bridging it draws a trend through
                    a week that has no reading. */}
                <Line type="monotone" dataKey="share" stroke={chart.emphasis} strokeWidth={2}
                      dot={{ r: 2.5, fill: chart.emphasis }} activeDot={{ r: 4 }}
                      connectNulls={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
            {mean !== null && (
              <div style={s.foot}>
                Averages {pct(mean)} across the {shares.length} {grain === 'month' ? 'months' : 'pay periods'} shown.
                The dashed line is that average.
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

const s = {
  title: { fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
           color: c.textMuted, margin: '26px 0 10px' },
  card:  { background: c.surface, border: `1px solid ${c.border}`, borderRadius: '12px',
           padding: '14px 10px 6px' },
  empty: { textAlign: 'center', padding: '28px', color: c.textFaint },
  foot:  { fontSize: '11px', color: c.textFaint, padding: '2px 6px 8px' },
};
